import { getLanguageFriendlyName } from '@lexical/code'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { InsertGapImageDialog } from '../GapImagePlugin'
import { mergeRegister } from '@lexical/utils'
import {
   CAN_REDO_COMMAND,
   CAN_UNDO_COMMAND,
   COMMAND_PRIORITY_CRITICAL,
   SELECTION_CHANGE_COMMAND,
} from 'lexical'
import { useEffect, useState } from 'react'
import { IS_APPLE } from '../../shared/environment'

import DropDown, { DropDownItem } from '../../ui/DropDown'
import { InsertNewTableDialog, InsertTableDialog } from '../TablePlugin'
import ColorPicker from '../../ui/ColorPicker'
import Box from '@mui/material/Box'
import { ToolbarShowOptions } from '../../LexicalTypes'
import { CODE_LANGUAGE_OPTIONS, blockTypeToBlockName } from './constants'
import { dropDownActiveClass } from './ToolbarPluginUtils'
import ToolbarFontDropDown from './ToolbarFontDropDown'
import ToolbarBlockFormatDropDown from './ToolbarBlockFormatDropDown'
import useInitialMount from 'reusable/hooks/useInitialMount'
import ToolbarPluginStore from './ToolbarPluginStore'
import useStore from 'reusable/mobx-store-utils'
import GapDialog from 'reusable/gap-dialog/GapDialog'
import { observer } from 'mobx-react-lite'

interface GapToolbarPluginProps {
   orgId: string,
   show?: ToolbarShowOptions[]
}

export default observer(function GapToolbarPlugin({ orgId, show = [] as ToolbarShowOptions[] }: GapToolbarPluginProps): JSX.Element {
   const [editor] = useLexicalComposerContext()
   const toolbarPluginStore = useStore(sp => new ToolbarPluginStore(sp), { orgId, editor, show })
   const [isEditable, setIsEditable] = useState(() => editor.isEditable())

   useInitialMount(() => {
      // Focus the editor to ensure it is fully loaded
      [editor, toolbarPluginStore.activeEditor].forEach(e => { e.focus(); e.blur() })
   })

   useEffect(() => {
      return editor.registerCommand(
         SELECTION_CHANGE_COMMAND,
         (_payload, newEditor) => {
            toolbarPluginStore.updateToolbar(newEditor)
            toolbarPluginStore.activeEditor = newEditor
            return false
         },
         COMMAND_PRIORITY_CRITICAL,
      )
   }, [editor, toolbarPluginStore, toolbarPluginStore.updateToolbar])

   useEffect(() => {
      return mergeRegister(
         editor.registerEditableListener((editable) => {
            setIsEditable(editable)
         }),
         toolbarPluginStore.activeEditor.registerUpdateListener(() => {
            toolbarPluginStore.updateToolbar()
         }),
         toolbarPluginStore.activeEditor.registerCommand<boolean>(
            CAN_UNDO_COMMAND,
            (payload) => {
               toolbarPluginStore.canUndo = payload
               return false
            },
            COMMAND_PRIORITY_CRITICAL,
         ),
         toolbarPluginStore.activeEditor.registerCommand<boolean>(
            CAN_REDO_COMMAND,
            (payload) => {
               toolbarPluginStore.canRedo = payload
               return false
            },
            COMMAND_PRIORITY_CRITICAL,
         ),
      )
   }, [toolbarPluginStore.activeEditor, editor, toolbarPluginStore, toolbarPluginStore.updateToolbar])

   const ShowToolbarExtension = ({ showIf, children }: { showIf: ToolbarShowOptions, children: JSX.Element }) => {
      return toolbarPluginStore.toolbarExtensions[showIf] || show.includes('*') ? children : null
   }

   return <Box className="toolbar">
      <ShowToolbarExtension showIf='Undo'>
         <button
            disabled={!toolbarPluginStore.canUndo || !isEditable}
            data-id="UNDO_COMMAND"
            onClick={toolbarPluginStore.dispatchCommand}
            title={IS_APPLE ? 'Undo (⌘Z)' : 'Undo (Ctrl+Z)'}
            type="button"
            className="toolbar-item spaced"
            aria-label="Undo">
            <i className="format undo" />
         </button>
      </ShowToolbarExtension>

      <ShowToolbarExtension showIf='Redo'>
         <button
            disabled={!toolbarPluginStore.canRedo || !isEditable}
            data-id="REDO_COMMAND"
            onClick={toolbarPluginStore.dispatchCommand}
            title={IS_APPLE ? 'Redo (⌘Y)' : 'Redo (Ctrl+Y)'}
            type="button"
            className="toolbar-item"
            aria-label="Redo">
            <i className="format redo" />
         </button>
      </ShowToolbarExtension>
      {toolbarPluginStore.blockType in blockTypeToBlockName && toolbarPluginStore.activeEditor === editor && (
         <ShowToolbarExtension showIf='BlockFormat'>
            <>
               <ToolbarBlockFormatDropDown
                  disabled={!isEditable}
                  blockType={toolbarPluginStore.blockType}
                  editor={editor}
               />
            </>
         </ShowToolbarExtension>
      )}
      {toolbarPluginStore.blockType === 'code' ? (
         <ShowToolbarExtension showIf='SelectLanguage'>
            <DropDown
               disabled={!isEditable}
               buttonClassName="toolbar-item code-language"
               buttonLabel={getLanguageFriendlyName(toolbarPluginStore.codeLanguage)}
               buttonAriaLabel="Select language">
               {CODE_LANGUAGE_OPTIONS.map(([value, name]) => {
                  return (
                     <DropDownItem
                        className={`item ${dropDownActiveClass(
                           value === toolbarPluginStore.codeLanguage,
                        )}`}
                        dataId={value}
                        onClick={toolbarPluginStore.onCodeLanguageSelect}
                        key={value}>
                        <span className="text">{name}</span>
                     </DropDownItem>
                  )
               })}
            </DropDown>
         </ShowToolbarExtension>
      ) : (
         <>
            <ShowToolbarExtension showIf='FontFamily'>
               <ToolbarFontDropDown
                  disabled={!isEditable}
                  style={'font-family'}
                  value={toolbarPluginStore.fontFamily}
                  editor={editor}
               />
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='FontSize'>
               <ToolbarFontDropDown
                  disabled={!isEditable}
                  style={'font-size'}
                  value={toolbarPluginStore.fontSize}
                  editor={editor}
               />
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='Bold'>
               <button
                  disabled={!isEditable}
                  data-id="FORMAT_TEXT_COMMAND"
                  data-type="bold"
                  onClick={toolbarPluginStore.dispatchCommand}
                  className={'toolbar-item spaced ' + (toolbarPluginStore.isBold ? 'active' : '')}
                  title={IS_APPLE ? 'Bold (⌘B)' : 'Bold (Ctrl+B)'}
                  type="button"
                  aria-label={`Format text as bold. Shortcut:'⌘B' : 'Ctrl+B'}`}>
                  <i className="format bold" />
               </button>
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='Italic'>
               <button
                  disabled={!isEditable}
                  data-id="FORMAT_TEXT_COMMAND"
                  data-type="italic"
                  onClick={toolbarPluginStore.dispatchCommand}
                  className={'toolbar-item spaced ' + (toolbarPluginStore.isItalic ? 'active' : '')}
                  title={IS_APPLE ? 'Italic (⌘I)' : 'Italic (Ctrl+I)'}
                  type="button"
                  aria-label={`Format text as italics. Shortcut: ${IS_APPLE ? '⌘I' : 'Ctrl+I'
                     }`}>
                  <i className="format italic" />
               </button>
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='Underline'>
               <button
                  disabled={!isEditable}
                  data-id="FORMAT_TEXT_COMMAND"
                  data-type="underline"
                  onClick={toolbarPluginStore.dispatchCommand}
                  className={'toolbar-item spaced ' + (toolbarPluginStore.isUnderline ? 'active' : '')}
                  title={IS_APPLE ? 'Underline (⌘U)' : 'Underline (Ctrl+U)'}
                  type="button"
                  aria-label={`Format text to underlined. Shortcut: ${IS_APPLE ? '⌘U' : 'Ctrl+U'
                     }`}>
                  <i className="format underline" />
               </button>
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='CodeBlock'>
               <button
                  disabled={!isEditable}
                  data-id="FORMAT_TEXT_COMMAND"
                  data-type="code"
                  onClick={toolbarPluginStore.dispatchCommand}
                  className={'toolbar-item spaced ' + (toolbarPluginStore.isCode ? 'active' : '')}
                  title="Insert code block"
                  type="button"
                  aria-label="Insert code block">
                  <i className="format code" />
               </button>
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='Link'>
               <button
                  disabled={!isEditable}
                  onClick={toolbarPluginStore.insertLink}
                  className={'toolbar-item spaced ' + (toolbarPluginStore.isLink ? 'active' : '')}
                  aria-label="Insert link"
                  title="Insert link"
                  type="button">
                  <i className="format link" />
               </button>
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='TextColor'>
               <ColorPicker
                  disabled={!isEditable}
                  buttonClassName="toolbar-item color-picker"
                  buttonAriaLabel="Formatting text colour"
                  buttonIconClassName="icon font-color"
                  color={toolbarPluginStore.fontColor}
                  onChange={toolbarPluginStore.onFontColorSelect}
                  title="text colour"
               />
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='BGColor'>
               <ColorPicker
                  disabled={!isEditable}
                  buttonClassName="toolbar-item color-picker"
                  buttonAriaLabel="Formatting background colour"
                  buttonIconClassName="icon bg-color"
                  color={toolbarPluginStore.bgColor}
                  onChange={toolbarPluginStore.onBgColorSelect}
                  title="bg colour"
               />
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='FormattingOptions'>
               <>
                  <DropDown
                     disabled={!isEditable}
                     buttonClassName="toolbar-item spaced"
                     buttonLabel=""
                     buttonAriaLabel="Formatting options for additional text styles"
                     buttonIconClassName="icon dropdown-more">
                     <ShowToolbarExtension showIf='Strikethrough'>
                        <DropDownItem
                           dataId="FORMAT_TEXT_COMMAND"
                           dataType='strikethrough'
                           onClick={toolbarPluginStore.dispatchCommand}
                           className={'item ' + dropDownActiveClass(toolbarPluginStore.isStrikethrough)}
                           title="Strikethrough"
                           aria-label="Format text with a strikethrough">
                           <i className="icon strikethrough" />
                           <span className="text">Strikethrough</span>
                        </DropDownItem>
                     </ShowToolbarExtension>

                     <ShowToolbarExtension showIf='Subscript'>
                        <DropDownItem
                           dataId="FORMAT_TEXT_COMMAND"
                           dataType="subscript"
                           onClick={toolbarPluginStore.dispatchCommand}
                           className={'item ' + dropDownActiveClass(toolbarPluginStore.isSubscript)}
                           title="Subscript"
                           aria-label="Format text with a subscript">
                           <i className="icon subscript" />
                           <span className="text">Subscript</span>
                        </DropDownItem>
                     </ShowToolbarExtension>

                     <ShowToolbarExtension showIf='Superscript'>
                        <DropDownItem
                           dataId="FORMAT_TEXT_COMMAND"
                           dataType="superscript"
                           onClick={toolbarPluginStore.dispatchCommand}
                           className={'item ' + dropDownActiveClass(toolbarPluginStore.isSuperscript)}
                           title="Superscript"
                           aria-label="Format text with a superscript">
                           <i className="icon superscript" />
                           <span className="text">Superscript</span>
                        </DropDownItem>
                     </ShowToolbarExtension>

                     <ShowToolbarExtension showIf='ClearFormatting'>
                        <DropDownItem
                           onClick={toolbarPluginStore.clearFormatting}
                           className="item"
                           title="Clear text formatting"
                           aria-label="Clear all text formatting">
                           <i className="icon clear" />
                           <span className="text">Clear Formatting</span>
                        </DropDownItem>
                     </ShowToolbarExtension>

                  </DropDown>
               </>
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='InsertOptions'>
               <DropDown
                  disabled={!isEditable}
                  buttonClassName="toolbar-item spaced"
                  buttonLabel="Insert"
                  buttonAriaLabel="Insert specialized editor node"
                  buttonIconClassName="icon plus">

                  <ShowToolbarExtension showIf='HorizontalRule'>
                     <DropDownItem
                        dataId="INSERT_HORIZONTAL_RULE_COMMAND"
                        onClick={toolbarPluginStore.dispatchCommand}
                        className="item">
                        <i className="icon horizontal-rule" />
                        <span className="text">Horizontal Rule</span>
                     </DropDownItem>
                  </ShowToolbarExtension>

                  <ShowToolbarExtension showIf='Image'>
                     <DropDownItem
                        onClick={toolbarPluginStore.insertImageModal.open}
                        className="item">
                        <i className="icon image" />
                        <span className="text">Image</span>
                     </DropDownItem>
                  </ShowToolbarExtension>

                  <ShowToolbarExtension showIf='Table'>
                     <DropDownItem
                        onClick={toolbarPluginStore.insertTableModal.open}
                        className="item">
                        <i className="icon table" />
                        <span className="text">Table</span>
                     </DropDownItem>
                  </ShowToolbarExtension>

                  <ShowToolbarExtension showIf='ExperimentalTable'>
                     <DropDownItem
                        onClick={toolbarPluginStore.insertExperimentalTabelModal.open}
                        className="item">
                        <i className="icon table" />
                        <span className="text">Table (Experimental)</span>
                     </DropDownItem>
                  </ShowToolbarExtension>
               </DropDown>
            </ShowToolbarExtension>
         </>
      )}

      <ShowToolbarExtension showIf='AlignmentOptions'>
         <DropDown
            disabled={!isEditable}
            buttonLabel="Align"
            buttonIconClassName="icon left-align"
            buttonClassName="toolbar-item spaced alignment"
            buttonAriaLabel="Formatting options for text alignment">

            <ShowToolbarExtension showIf='AlignLeft'>
               <DropDownItem
                  dataId="FORMAT_ELEMENT_COMMAND"
                  dataType="left"
                  onClick={toolbarPluginStore.dispatchCommand}
                  className="item">
                  <i className="icon left-align" />
                  <span className="text">Left Align</span>
               </DropDownItem>
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='AlignCenter'>
               <DropDownItem
                  dataId="FORMAT_ELEMENT_COMMAND"
                  dataType="center"
                  onClick={toolbarPluginStore.dispatchCommand}
                  className="item">
                  <i className="icon center-align" />
                  <span className="text">Center Align</span>
               </DropDownItem>
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='AlignRight'>
               <DropDownItem
                  dataId="FORMAT_ELEMENT_COMMAND"
                  dataType="right"
                  onClick={toolbarPluginStore.dispatchCommand}
                  className="item">
                  <i className="icon right-align" />
                  <span className="text">Right Align</span>
               </DropDownItem>
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='AlignJustify'>
               <DropDownItem
                  dataId="FORMAT_ELEMENT_COMMAND"
                  dataType="justify"
                  onClick={toolbarPluginStore.dispatchCommand}
                  className="item">
                  <i className="icon justify-align" />
                  <span className="text">Justify Align</span>
               </DropDownItem>
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='AlignOutdent'>
               <DropDownItem
                  dataId="OUTDENT_CONTENT_COMMAND"
                  onClick={toolbarPluginStore.dispatchCommand}
                  className="item">
                  <i className={'icon ' + (toolbarPluginStore.isRTL ? 'indent' : 'outdent')} />
                  <span className="text">Outdent</span>
               </DropDownItem>
            </ShowToolbarExtension>

            <ShowToolbarExtension showIf='AlignIndent'>
               <DropDownItem
                  dataId="INDENT_CONTENT_COMMAND"
                  onClick={toolbarPluginStore.dispatchCommand}
                  className="item">
                  <i className={'icon ' + (toolbarPluginStore.isRTL ? 'outdent' : 'indent')} />
                  <span className="text">Indent</span>
               </DropDownItem>
            </ShowToolbarExtension>
         </DropDown>
      </ShowToolbarExtension>

      <GapDialog modalState={toolbarPluginStore.insertImageModal} title='Insert Image'>
         <InsertGapImageDialog
            activeEditor={toolbarPluginStore.activeEditor}
            onClose={toolbarPluginStore.insertImageModal.close}
            orgId={orgId}
         />
      </GapDialog>

      <GapDialog modalState={toolbarPluginStore.insertTableModal} title='Insert Table'>
         <InsertTableDialog
            activeEditor={toolbarPluginStore.activeEditor}
            onClose={toolbarPluginStore.insertTableModal.close}
         />
      </GapDialog>

      <GapDialog modalState={toolbarPluginStore.insertExperimentalTabelModal} title='Insert Table'>
         <InsertNewTableDialog
            activeEditor={toolbarPluginStore.activeEditor}
            onClose={toolbarPluginStore.insertExperimentalTabelModal.close}
         />
      </GapDialog>
   </Box>
})
